import { Check, MoreHoriz } from "@mui/icons-material"
import { Box, Button, Stack, TableRow, Tooltip, Typography } from "@mui/material"

import { TableCell } from "@mui/material"

import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"

import { readAgentInfo, Rebalancing } from "@/api/portfolio-agents-api"
import { InMemoryTable } from "@/components/InMemoryTable"
import { LoadingSkeletons } from "@/components/LoadingSkeletons"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { TokenBlock } from "@/components/TokenBlock"
import { formatFullDate, formatRelative } from "@/utils/format"

type RebalanceHistoryProps = {
  entityId: string
}

export function RebalanceHistory(props: RebalanceHistoryProps) {
  const { entityId } = props

  const { data: agentInfo } = useQuery({
    queryKey: ["agent_info", entityId],
    queryFn: async () => {
      // console.log("Fetching Agent Info", entityId)
      const res = await readAgentInfo(entityId)
      console.log("onAgentInfo", entityId, res)
      return res
    },
    enabled: !!entityId,
  })

  const data = useMemo(() => agentInfo?.Rebalancings?.reverse() || [], [agentInfo])
  const [visibleCount, setVisibleCount] = useState(5)
  const visibleData = useMemo(() => data.slice(0, visibleCount), [data, visibleCount])

  // TODO
  const quoteTokenAddress =
    agentInfo && typeof agentInfo["Quote-Token"] === "object"
      ? (agentInfo["Quote-Token"] as any).token
      : agentInfo?.["Quote-Token"]

  const allTokens = useMemo(
    () =>
      agentInfo
        ? [...(agentInfo.PortfolioTokens?.map((x) => x.token) || []), quoteTokenAddress]
        : [],
    [agentInfo],
  )

  return (
    <>
      <Typography
        sx={{
          marginTop: 2,
          marginBottom: 1,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
        variant="h4"
      >
        Rebalance History
      </Typography>
      {!agentInfo ? (
        <LoadingSkeletons />
      ) : (
        <Box
          sx={(theme) => ({
            // background: "var(--mui-palette-paperAlt)",
            overflow: "auto",

            "& th:nth-child(2)": {
              background: "var(--mui-palette-paperAlt)",
              position: "sticky",
              zIndex: 1,
              left: 0,
              // borderRight: "1px solid var(--mui-palette-divider)",
            },
            "& td:nth-child(2)": {
              background: "var(--mui-palette-paperAlt)",
              position: "sticky",
              zIndex: 1,
              left: 0,
              // borderRight: "1px solid var(--mui-palette-divider)",
            },
          })}
        >
          <InMemoryTable
            initialSortField="value"
            initialSortDir="desc"
            data={visibleData}
            headerCells={[
              { label: "#", align: "right", sx: { width: 50 } },
              { label: "Date", sx: { minWidth: 140 } },
              ...allTokens.map((token) => ({
                label: (
                  <Typography variant="inherit" color="text.secondary" component="div">
                    <Stack alignItems="flex-end">
                      <TokenBlock tokenId={token} hideCopyToClipboard />
                    </Stack>
                  </Typography>
                ),
                sx: { minWidth: 220 },
              })),
              { label: "", align: "right", sx: { width: 50 } },
            ]}
            renderRow={(row: Rebalancing, index) => (
              <TableRow
                key={row.completedAt}
                sx={{
                  background: "var(--mui-palette-paperAlt)",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                <TableCell
                  align="right"
                  variant="head"
                  sx={{ borderBottom: "1px solid var(--mui-palette-TableCell-border)" }}
                >
                  {index + 1}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                  }}
                >
                  <Tooltip title={formatFullDate(row.completedAt)}>
                    <span>{formatRelative(row.completedAt)}</span>
                  </Tooltip>
                </TableCell>
                {allTokens.map((token) => {
                  const balanceBefore =
                    row.holdingsBefore.entries.find((x) => x.token === token)?.amount || "0"

                  const balanceAfter =
                    row.holdingsAfter.entries.find((x) => x.token === token)?.amount || "0"

                  const delta = BigInt(balanceAfter) - BigInt(balanceBefore)

                  return (
                    <TableCell align="right" variant="number" key={token}>
                      <TokenAmountBlock
                        amount={balanceAfter}
                        tokenId={token}
                        hideCopyToClipboard
                        needsParsing
                      />
                      {delta !== 0n && (
                        <>
                          (
                          <TokenAmountBlock
                            amount={delta}
                            tokenId={token}
                            hideCopyToClipboard
                            needsParsing
                            showSignAlways
                            color={delta > 0n ? "success.main" : "warning.main"}
                          />
                          )
                        </>
                      )}
                    </TableCell>
                  )
                })}
                <TableCell align="right">
                  {row.holdingsAfter.isUnbalanced ? (
                    <Tooltip title="After performing these swaps, the portfolio remained unbalanced.">
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          marginLeft: "auto",
                          borderRadius: 0.5,
                          height: 22,
                          width: 22,
                          background: "rgba(var(--mui-palette-warning-mainChannel) / 0.05)",
                          border: "1px solid",
                          borderColor: "var(--mui-palette-warning-main)",
                          "& svg": {
                            width: "0.75rem",
                            height: "0.75rem",
                          },
                        }}
                      >
                        <MoreHoriz style={{ color: "var(--mui-palette-warning-main)" }} />
                      </Stack>
                    </Tooltip>
                  ) : (
                    <Tooltip title="After performing these swaps, the portfolio became balanced.">
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          marginLeft: "auto",
                          borderRadius: 0.5,
                          height: 22,
                          width: 22,
                          background: "rgba(var(--mui-palette-success-mainChannel) / 0.1)",
                          border: "1px solid",
                          borderColor: "var(--mui-palette-success-main)",
                          "& svg": {
                            width: "0.75rem",
                            height: "0.75rem",
                          },
                        }}
                      >
                        <Check style={{ color: "var(--mui-palette-accent-main)" }} />
                      </Stack>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            )}
          />
        </Box>
      )}
      {visibleCount < data.length && (
        <Button
          onClick={() => {
            setVisibleCount((prev) => prev + 5)
          }}
          variant="outlined"
          color="inherit"
          size="small"
          sx={{
            alignSelf: "center",
            textTransform: "none",
          }}
        >
          Load more records
        </Button>
      )}
    </>
  )
}
